import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from '@react';
import { useAuth, useDocument, useI18n } from '@react/hooks';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  ErrorAlert,
  Input,
} from '@react/components';
import { EntityAvatar } from '@/components/EntityAvatar';
import { AcknowledgmentCeremony } from '@/features/document/AcknowledgmentCeremony';
import { AcknowledgmentRecipients, type AckRecipient } from '@/features/document/AcknowledgmentRecipients';
import {
  ackActorId,
  computeAckStats,
  currentAcknowledgments,
  findPendingForUser,
  groupAcknowledgmentsByVersion,
  normalizeAckStatus,
  parseDocumentVersion,
  type AckStatus,
} from '@/features/document/acknowledgment';
import { formatDate, formatRelativeTime, getDocumentVersion } from '@/features/document/logic';
import { UserName } from '@/features/userprofile/UserName';
import { useScope } from '@/hooks/useScope';

type StatusFilter = 'ALL' | AckStatus;

const STATUS_CONFIG: Record<AckStatus, { variant: 'warning' | 'success' | 'error' | 'secondary'; labelKey: string }> = {
  PENDING: { variant: 'warning', labelKey: 'document.ackPending' },
  ACKNOWLEDGED: { variant: 'success', labelKey: 'document.ackAcknowledged' },
  OVERDUE: { variant: 'error', labelKey: 'document.ackOverdue' },
  WAIVED: { variant: 'secondary', labelKey: 'document.ackWaived' },
};

function isDueSoon(dueDate: string | undefined): boolean {
  if (!dueDate) return false;
  const diff = new Date(dueDate).getTime() - Date.now();
  return diff > 0 && diff < 3 * 24 * 60 * 60 * 1000;
}

function ProgressBar({ acknowledged, total }: { acknowledged: number; total: number }) {
  const { t } = useI18n();
  if (total === 0) return null;
  const percent = Math.round((acknowledged / total) * 100);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{acknowledged === total ? t('document.ackAllDone') : t('document.ackProgress', { count: acknowledged, total })}</span>
        <span className="tabular-nums">{percent}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
        <div className={`h-full rounded-full ${percent === 100 ? 'bg-success' : 'bg-primary'}`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function AckRow({ item, locale }: { item: any; locale: string }) {
  const { t } = useI18n();
  const status = normalizeAckStatus(item.status);
  const config = STATUS_CONFIG[status];
  const actorId = ackActorId(item);
  const name = item.actor?.display_name || t('document.ackLegacyUnknown');
  const dueSoon = status === 'PENDING' && isDueSoon(item.due_date);
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 last:border-b-0">
      <EntityAvatar name={name} image={item.actor?.avatar} profileId={actorId} size="sm" className="h-8 w-8" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-foreground">
          {actorId && !item.actor?.display_name
            ? <UserName id={actorId} showIdFallback={false} fallbackLabel={t('document.ackLegacyUnknown')} linkToProfile className="truncate" />
            : <span className="truncate">{name}</span>}
        </div>
        {item.comment && <p className="mt-0.5 truncate text-xs text-muted-foreground">{item.comment}</p>}
      </div>
      {dueSoon && (
        <span className="text-xs tabular-nums text-warning" title={t('document.ackDueDate')}>
          {formatDate(item.due_date, locale)}
        </span>
      )}
      <Badge variant={config.variant} size="sm">{t(config.labelKey)}</Badge>
      <span className="w-24 text-right text-xs tabular-nums text-muted-foreground">
        {status === 'ACKNOWLEDGED'
          ? formatRelativeTime(item.acknowledged_at, locale)
          : formatRelativeTime(item.requested_at, locale)}
      </span>
    </div>
  );
}

export const DocumentAcknowledgmentListPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useDocument();
  const scope = useScope();
  const { userId } = useAuth();
  const { t, locale } = useI18n();
  const [acknowledgments, setAcknowledgments] = useState<any[]>([]);
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentVersion, setDocumentVersion] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestData, setRequestData] = useState({ due_date: '', message: '' });
  const [recipients, setRecipients] = useState<AckRecipient[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [ceremonyOpen, setCeremonyOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setLoadError(null);
    const [ackResult, documentResult] = await Promise.allSettled([
      store.ListAcknowledgments('document', id, { pagination: { limit: 1000, cursor: '', direction: '' } }),
      store.GetDocument(id),
    ]);
    if (ackResult.status === 'fulfilled') {
      setAcknowledgments(ackResult.value.acknowledgments?.acknowledgments ?? []);
    } else {
      setLoadError(ackResult.reason);
    }
    if (documentResult.status === 'fulfilled') {
      setDocumentTitle(documentResult.value.title);
      setDocumentVersion(parseDocumentVersion(getDocumentVersion(documentResult.value)));
    }
    setIsLoading(false);
  }, [id, store]);

  useEffect(() => {
    void load();
  }, [load]);

  const people = useMemo(() => currentAcknowledgments(acknowledgments), [acknowledgments]);
  const stats = useMemo(() => computeAckStats(people), [people]);
  const filtered = useMemo(
    () => statusFilter === 'ALL' ? people : people.filter((ack) => normalizeAckStatus(ack.status) === statusFilter),
    [people, statusFilter],
  );
  const groups = useMemo(() => groupAcknowledgmentsByVersion(filtered), [filtered]);
  const currentVersion = documentVersion || groupAcknowledgmentsByVersion(acknowledgments)[0]?.version || 0;
  const pending = findPendingForUser(acknowledgments, userId ?? '', currentVersion);

  const handleRequestAcknowledgment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!id || recipients.length === 0 || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await store.RequestAcknowledgment('document', id, {
        actors: {
          actors: recipients.map((recipient) => ({
            entity: { id: recipient.id, type_id: '', type_name: 'profile', platform_id: '', tenant_id: '', product_id: '' },
            display_name: recipient.name,
            avatar: '',
          })),
        },
        due_date: requestData.due_date ? new Date(requestData.due_date).toISOString() : undefined,
        message: requestData.message || undefined,
      });
      setShowRequestForm(false);
      setRequestData({ due_date: '', message: '' });
      setRecipients([]);
      await load();
    } catch (error) {
      setLoadError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filters: { id: StatusFilter; label: string; count: number }[] = [
    { id: 'ALL', label: t('document.ackFilterAll'), count: stats.total },
    { id: 'PENDING', label: t('document.ackPending'), count: stats.pending },
    { id: 'ACKNOWLEDGED', label: t('document.ackAcknowledged'), count: stats.acknowledged },
    { id: 'OVERDUE', label: t('document.ackOverdue'), count: stats.overdue },
  ];

  return (
    <div className="mx-auto max-w-[900px] space-y-5 px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="sr-only">{t('document.acknowledgments')}</h1>
        {pending && (
          <Button variant="secondary" size="sm" className="ml-auto" onClick={() => setCeremonyOpen(true)}>
            {t('document.ack.openAndConfirm')}
          </Button>
        )}
        <Button size="sm" className={pending ? '' : 'ml-auto'} onClick={() => setShowRequestForm((open) => !open)}>
          {t('document.requestAcknowledgment')}
        </Button>
      </div>

      {loadError != null && (
        <div className="space-y-2">
          <ErrorAlert error={loadError} dismissible onDismiss={() => setLoadError(null)} />
          <Button variant="secondary" size="sm" onClick={() => void load()}>{t('errors.tryAgain')}</Button>
        </div>
      )}
      {store.error && !loadError ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {showRequestForm && (
        <form onSubmit={handleRequestAcknowledgment}>
          <Card>
            <CardHeader>
              <CardTitle>{t('document.requestAcknowledgment')}</CardTitle>
              <CardDescription>{t('document.requestAcknowledgmentDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <AcknowledgmentRecipients documentId={id ?? ''} value={recipients} onChange={setRecipients} />
              <Input
                label={t('document.ackDueDate')}
                type="date"
                value={requestData.due_date}
                onChange={(event) => setRequestData((current) => ({ ...current, due_date: event.target.value }))}
                placeholder={t('document.ackDueDatePlaceholder')}
              />
              <div className="space-y-1.5">
                <label htmlFor="ack-request-comment" className="block text-sm font-medium text-foreground">{t('document.ackComment')}</label>
                <textarea
                  id="ack-request-comment"
                  className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={requestData.message}
                  onChange={(event) => setRequestData((current) => ({ ...current, message: event.target.value }))}
                  placeholder={t('document.ackCommentPlaceholder')}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={() => setShowRequestForm(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={isSubmitting || recipients.length === 0} isLoading={isSubmitting}>
                {t('document.ackSendRequest')}
              </Button>
            </CardFooter>
          </Card>
        </form>
      )}

      {!isLoading && stats.total > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('document.ackStatus')}>
          {filters.map((filter) => (
            <button
              key={filter.id}
              type="button"
              aria-pressed={statusFilter === filter.id}
              onClick={() => setStatusFilter(filter.id)}
              className={`rounded-full border px-3 py-1.5 text-xs tabular-nums ${
                statusFilter === filter.id
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              {filter.label} · {filter.count}
            </button>
          ))}
        </div>
      )}

      {isLoading ? (
        <Card aria-busy="true">
          <CardContent className="space-y-3 py-5">
            {Array.from({ length: 4 }).map((_, index) => <div key={index} className="h-10 animate-pulse rounded bg-muted" />)}
          </CardContent>
        </Card>
      ) : stats.total === 0 && !loadError ? (
        <Card>
          <CardContent className="py-12 text-center">
            <h2 className="text-base font-medium text-foreground">{t('document.ackEmptyTitle')}</h2>
            <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{t('document.ackEmptyDescription')}</p>
            <Button className="mt-5" onClick={() => setShowRequestForm(true)}>{t('document.requestAcknowledgment')}</Button>
          </CardContent>
        </Card>
      ) : groups.length === 0 && !loadError ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">{t('common.noResults')}</CardContent></Card>
      ) : (
        groups.map((group) => {
          const acknowledged = group.items.filter((ack) => normalizeAckStatus(ack.status) === 'ACKNOWLEDGED').length;
          return (
            <Card key={group.version}>
              <CardHeader>
                <CardTitle className="text-sm tabular-nums">
                  {group.version > 0
                    ? t('document.ack.versionHeading', { version: group.version })
                    : t('document.ack.legacyVersion')}
                </CardTitle>
                <CardDescription>{t('document.ack.progress', { done: acknowledged, total: group.items.length })}</CardDescription>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <div className="px-6 pb-4"><ProgressBar acknowledged={acknowledged} total={group.items.length} /></div>
                <div>
                  {group.items.map((item, index) => (
                    <AckRow key={item.entity?.id || item.id || index} item={item} locale={locale} />
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      {ceremonyOpen && pending && id && (
        <AcknowledgmentCeremony
          documentId={id}
          productId={scope.productId}
          documentTitle={documentTitle || t('common.untitled')}
          acknowledgment={pending}
          onCancel={() => setCeremonyOpen(false)}
          onCompleted={() => {
            setCeremonyOpen(false);
            void load();
          }}
        />
      )}
    </div>
  );
});

export default DocumentAcknowledgmentListPage;
